import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { AlertCircle, Info, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface CollapsibleEmailLoginFormProps {
  email: string;
  password: string;
  isSubmitting: boolean;
  isResetting: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onResetPassword: (e: React.FormEvent) => void;
}

export const CollapsibleEmailLoginForm = ({
  email,
  password,
  isSubmitting,
  isResetting,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onResetPassword,
}: CollapsibleEmailLoginFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsWarning, setShowTermsWarning] = useState(false);
  const isMobile = useIsMobile();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      setShowTermsWarning(true);
      return;
    } else {
      setShowTermsWarning(false);
    }
    
    onSubmit(e);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    onResetPassword(e);
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-center relative text-muted-foreground text-sm py-2"
          >
            Use e-mail and password
            <div className="absolute right-3">
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="rounded-md shadow-sm space-y-4 mt-4">
            <div>
              <label htmlFor="email" className="sr-only">
                E-mail address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm text-center"
                placeholder="E-mail address"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm text-center"
                placeholder="Password"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
              />
            </div>
            
            <div className="flex items-center justify-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-primary hover:text-primary-light"
                onClick={handleResetPassword}
                disabled={isResetting}
              >
                {isResetting ? 'Sending reset instructions...' : 'Forgot your password?'}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Always visible elements */}
      <div className={`flex items-start mt-4 ${showTermsWarning ? 'bg-red-50 p-2 rounded-md border border-red-200' : ''}`}>
        <div className="flex items-center h-5">
          <Checkbox 
            id="terms"
            checked={agreedToTerms}
            onCheckedChange={(checked) => {
              setAgreedToTerms(checked === true);
              if (checked === true) {
                setShowTermsWarning(false);
              }
            }}
            className={`h-4 w-4 ${showTermsWarning ? 'border-red-400' : ''}`}
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="terms" className={`${isMobile ? 'text-xs' : 'text-gray-600'} flex flex-nowrap whitespace-nowrap ${showTermsWarning ? 'text-red-600 font-medium' : ''}`}>
            I agree to the <Link to="/terms" className="text-primary hover:underline mx-1" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy" className="text-primary hover:underline mx-1" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
          </label>
        </div>
      </div>
      
      {showTermsWarning && (
        <div className="flex items-center text-red-500 text-sm mt-1">
          <AlertCircle className="h-4 w-4 mr-1" />
          <span>Please agree to the terms and privacy policy to continue</span>
        </div>
      )}

      <div>
        <Button
          type="submit"
          className={`w-full ${!agreedToTerms ? 'opacity-70' : ''}`}
          disabled={isSubmitting || !agreedToTerms}
        >
          {isSubmitting ? 'Signing in...' : 'Log In'}
        </Button>
        
        <p className="text-center text-sm text-gray-600 mt-4">
          New here?{' '}
          <Link
            to="/signup"
            className="text-primary hover:text-primary-dark font-medium"
          >
            Create an account
          </Link>
        </p>
      </div>
      
      
    </form>
  );
};